import { PAYMENT_OPTIONS } from 'constants/payment'
import { floatify } from 'lib/maths/mathUtils'
import { createSelector } from 'reselect'
import { isSpoofed } from 'selectors/featuresSelectors'
import getPayableAfterModifiers from './getPayableAfterModifiers'
import getCreditBalanceForCheckoutCurrency from 'checkout/selectors/payment/getCreditBalanceForCheckoutCurrency'
import getCreditPayAmount from './getCreditPayAmount'
import getDepositAmountBeforeCreditApplied from './deposit/getDepositAmountBeforeCreditApplied'
import { getLuxPlusSubscriptionsTotalPrice } from '../view/luxPlusSubscription'
import { getSubscriptionJoinFeeTotals } from './luxPlusSubscription'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import getPaymentScheduleMerchantFeeEligibleAmount from './paymentSchedule/getPaymentScheduleMerchantFeeEligibleAmount'
import { getFormattedLuxPlusSubscriptionItems } from './orderCreationSelectors'
import { sum } from 'lib/array/arrayUtils'
import { getCheckoutLoyaltyBurnTotalInCurrency } from '../loyalty/getCheckoutLoyaltyBurnTotals'
import getGiftCardPayAmount from './getGiftCardPayAmount'
import { shouldShowDebitCardMerchantFees } from './paymentType'
import getDynamicPaymentScheduleMerchantFeeEligibleAmount from './paymentSchedule/getDynamicPaymentScheduleMerchantFeeElgibleAmount'
import { getPayAtPropertyAmount } from './propertyCollectSelectors'

const getExcludedMerchantFeeTotal = createSelector(
  (state: App.State) => getLuxPlusSubscriptionsTotalPrice(state),
  (state: App.State) => getSubscriptionJoinFeeTotals(state),
  (state: App.State) => getFormattedLuxPlusSubscriptionItems(state),
  (state: App.State) => getCheckoutLoyaltyBurnTotalInCurrency(state),
  (
    luxPlusTotal,
    joinFeeTotals,
    formattedLuxPlusSubscriptionItems,
    loyaltyBurnTotalInCurrency,
  ) => {
    const luxPlusPromoAmount = sum(formattedLuxPlusSubscriptionItems, item => sum(item.item_discounts ?? [], discount => discount.discount_amount))
    return floatify(luxPlusTotal + joinFeeTotals.data.price - luxPlusPromoAmount + loyaltyBurnTotalInCurrency)
  },
)

const getMerchantFeeAmount = createSelector(
  (state: App.State) => getDepositAmountBeforeCreditApplied(state),
  (state: App.State) => getCreditPayAmount(state),
  (state: App.State) => getGiftCardPayAmount(state),
  (state: App.State) => getPayableAfterModifiers(state),
  (state: App.State) => state.system.merchantFeeConfigs,
  (state: App.State, paymentType: App.PaymentOption, merchantFeePaymentType: App.MerchantFeePaymentType | null = null) => merchantFeePaymentType || state.checkout.merchantFeePaymentType,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (_state: App.State, paymentType: App.PaymentOption) => paymentType,
  (state: App.State) => getExcludedMerchantFeeTotal(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.checkout.payment.rebookingID,
  (state: App.State) => state.checkout.payment.useCredit,
  (state: App.State) => getCreditBalanceForCheckoutCurrency(state),
  (state: App.State) => getPaymentScheduleMerchantFeeEligibleAmount(state),
  (state: App.State) => getDynamicPaymentScheduleMerchantFeeEligibleAmount(state),
  (state: App.State) => shouldShowDebitCardMerchantFees(state),
  (state: App.State) => state.checkout.payment.useGiftCard,
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getPayAtPropertyAmount(state),
  (
    depositAmount,
    creditAmount,
    giftCardAmount,
    payableAfterModifiers,
    merchantFeeConfigs,
    merchantFeePaymentType,
    postPurchase,
    paymentType,
    excludedTotals,
    isSpoofed,
    rebookingID,
    useLECredits,
    creditBalance,
    paymentScheduleMerchantFeeEligibleAmount,
    dynamicPaymentScheduleMerchantFeeEligibleAmount,
    shouldShowDebitCardMerchantFees,
    useGiftCard,
    existingOrder,
    payAtPropertyAmount,
  ) => {
    // Don't show merchant fees for post purchases, except for custom offers with merchant fees enabled
    // and charter upgrades which support credit card payment fees
    if (postPurchase && postPurchase !== 'charter-upgrade') {
      if (postPurchase === 'payment-schedule-balance') return 0
      const customOfferMerchantFeeEnabled = existingOrder?.customOfferItems?.[0]?.custom_offer?.payment_metadata?.merchant_fee_enabled
      if (!customOfferMerchantFeeEnabled) return 0
    }
    if (paymentType === PAYMENT_OPTIONS.RESERVE_FOR_FREE) return 0
    if (!merchantFeePaymentType || merchantFeeConfigs.length === 0) return 0

    // Get merchant fee percentage from backend config, with debit card fee control
    const merchantFeePercentage = (merchantFeePaymentType === 'mastercardDebit' || merchantFeePaymentType === 'visaDebit') && !shouldShowDebitCardMerchantFees
      ? 0
      : merchantFeeConfigs.find((config) => config.id === merchantFeePaymentType)?.percentage
    if (!merchantFeePercentage) {
      return 0
    }

    let payableStripeTotal = 0
    // For post-purchase custom offers with full payment, use the payable amount (full order total).
    // For payment_schedule, fall through to the switch so merchant fee is based on the deposit only.
    if (postPurchase && existingOrder?.customOfferItems?.[0] && paymentType !== PAYMENT_OPTIONS.PAYMENT_SCHEDULE) {
      payableStripeTotal = floatify(payableAfterModifiers - creditAmount - giftCardAmount - excludedTotals)
      if (payableStripeTotal < 0) {
        payableStripeTotal = 0
      }
    } else {
      switch (paymentType) {
        case PAYMENT_OPTIONS.FULL:
          payableStripeTotal = floatify(payableAfterModifiers - payAtPropertyAmount - creditAmount - giftCardAmount - excludedTotals)
          if (payableStripeTotal < 0) {
            payableStripeTotal = 0
          }
          break
        case PAYMENT_OPTIONS.DEPOSIT:
          payableStripeTotal = (isSpoofed && rebookingID && (useLECredits || useGiftCard)) ?
              Math.max(0, floatify(depositAmount - creditBalance - giftCardAmount - excludedTotals)) : floatify(depositAmount - excludedTotals)
          break
        case PAYMENT_OPTIONS.PAYMENT_SCHEDULE:
          payableStripeTotal = floatify(paymentScheduleMerchantFeeEligibleAmount - excludedTotals)
          break
        case PAYMENT_OPTIONS.DYNAMIC_PAYMENT_SCHEDULE:
          payableStripeTotal = floatify(dynamicPaymentScheduleMerchantFeeEligibleAmount - excludedTotals)
          break
      }
    }

    return floatify(payableStripeTotal * (merchantFeePercentage / 100))
  },

)

export default getMerchantFeeAmount
